import { popupTargetFromElement } from '@blocksuite/affine-components/context-menu';
import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { css } from '@emotion/css';
import { computed, effect, signal } from '@preact/signals-core';
import { type TemplateResult } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import {
  CanvasViewportParticipantController,
  type CanvasViewportRowAnchorRect,
  type CanvasViewportTargetPosition,
  createElementCanvasViewportTarget,
  createRowAnchoredCanvasViewportTarget,
  readRowAnchoredCanvasViewportPosition,
} from '../../../core/canvas-viewport.js';
import {
  DATA_SOURCE_KANBAN_GROUP_RENDER_LIMIT,
  DATA_SOURCE_KANBAN_ROW_GROUP_RENDER_LIMIT,
} from '../../../core/data-source/window.js';
import {
  type GroupTrait,
  groupTraitKey,
} from '../../../core/group-by/trait.js';
import {
  createUniComponentFromWebComponent,
  dataViewI18nT,
} from '../../../core/index.js';
import {
  computeGroupedPostDropShifts,
  LivePreviewReorderController,
  type ReorderGeometry,
  type ReorderHost,
  type ReorderShiftItem,
} from '../../../core/row-reorder/index.js';
import {
  getShiftKeyActive$,
  RowSelectionController,
  type RowSelectionHost,
} from '../../../core/row-selection/index.js';
import { defaultActivators } from '../../../core/utils/wc-dnd/sensors/index.js';
import {
  createSortContext,
  sortable,
} from '../../../core/utils/wc-dnd/sort/sort-context.js';
import {
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '../../../core/utils/wc-dnd/sort/strategies/index.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
} from '../../../core/view/data-view-base.js';
import {
  dataViewBodyStateValue,
  getDataViewBodyElement,
  measureDataViewBodyIntrinsicHeight,
} from '../../../core/view/data-view-body.js';
import { slimDataViewScrollbarStyle } from '../../shared/slim-scrollbar-style.js';
import { popAddGroupInput } from '../add-group-menu.js';
import { resolveKanbanVisibleGroups } from '../card-render-window.js';
import {
  group2TraitKey,
  type KanbanSingleView,
} from '../kanban-view-manager.js';
import type { KanbanViewSelectionWithType } from '../selection.js';
import { KanbanClipboardController } from './controller/clipboard.js';
import { KanbanDragController } from './controller/drag.js';
import { KanbanHotkeysController } from './controller/hotkeys.js';
import { KanbanSelectionController } from './controller/selection.js';
import { measureKanbanRowsContentHeight } from './intrinsic-height.js';
import {
  KANBAN_GROUP_HEADER_HEIGHT,
  kanbanRowKeyFromSortId,
  kanbanRowSortId,
} from './layout.js';

interface KanbanCardRect {
  readonly cardId: string;
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly height: number;
}

interface KanbanGroupRect {
  readonly groupKey: string;
  readonly rowKey: string;
  /**
   * Combined scope key used by the shared reorder controller as the
   * drop-target id. Format: `${rowKey}::${colKey}`. Uniquely identifies
   * one cell in the 2-D grid; the drop handler splits it to drive
   * the atomic (L1, L2) property write.
   */
  readonly scope: string;
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
  /**
   * Top of the `.group-body` content area (below the group header).
   * Used to place cross-group dragged cards into an empty target
   * column where no existing card rect can anchor the block.
   */
  readonly bodyTop: number;
  /** Left of the `.group-body` content area — the cards' native x. */
  readonly bodyLeft: number;
  readonly cards: readonly KanbanCardRect[];
}

type KanbanLayoutSnapshot = readonly KanbanGroupRect[];

const KANBAN_CARD_GAP = 12;
const KANBAN_CANVAS_Y_SCROLL_TARGET = 'kanban-y';
const KANBAN_CANVAS_FLAT_X_SCROLL_TARGET = 'kanban-x';
const KANBAN_CANVAS_ROW_X_SCROLL_TARGET_PREFIX = 'kanban-row-x:';
const KANBAN_HORIZONTAL_SCROLL_TARGET_SELECTOR =
  '[data-kanban-canvas-x-scroll="flat"], .row-body-scroll[data-row-key]';

/**
 * Curated `CSSStyleDeclaration` props we copy onto inert clone divs
 * so the preview reproduces the source card's rendered look. Declared
 * at module scope (not inside the function) so the array is allocated
 * once per package load, not per descendant traversed.
 */
const CLONE_VISUAL_PROPS = [
  'display',
  'flexDirection',
  'flexWrap',
  'alignItems',
  'justifyContent',
  'gap',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'borderRadius',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderLeft',
  'background',
  'backgroundColor',
  'backgroundImage',
  'color',
  'fontSize',
  'fontWeight',
  'fontFamily',
  'lineHeight',
  'width',
  'height',
  'minWidth',
  'minHeight',
  'boxShadow',
  'opacity',
  'textAlign',
  'whiteSpace',
  'overflow',
  'textOverflow',
  'position',
  'top',
  'right',
  'bottom',
  'left',
] as const;

/**
 * Visual-only copy of a custom-element tree, safe to append to
 * `document.body`. `cloneNode(true)` re-instantiates nested Lit
 * elements (`affine-data-view-kanban-card`, `-cell`,
 * `header-area-text-cell`) whose `connectedCallback`s then crash
 * reading JS-ref properties (`kanbanViewLogic`, `column`) that
 * don't survive a clone. Instead we walk the DOM, swap every
 * hyphenated tag for a `<div>`, carry attributes (minus identity
 * ones), and inline the rendered `getComputedStyle` values on
 * every element so tag-targeting CSS rules still render correctly
 * after the tag change.
 */
function buildInertVisualClone(src: HTMLElement): HTMLElement {
  // SVG elements live in a different namespace than HTML — a naive
  // `document.createElement('svg')` produces an HTML element named
  // "svg" which doesn't render as a vector graphic. Icon glyphs
  // (status pill, progress, avatars, chevrons) would silently turn
  // into invisible divs in the drag ghost. `cloneNode(true)` on the
  // SVG root preserves the namespace chain for the whole subtree
  // AND keeps attributes like `viewBox`, `fill`, `stroke` intact —
  // which is exactly what the visual clone needs. Inlined computed
  // styles aren't necessary here because SVG inherits `color` / `fill`
  // through `currentColor` from the ancestor div whose computed
  // styles we already copy.
  if (src instanceof SVGElement) {
    return src.cloneNode(true) as unknown as HTMLElement;
  }
  const tag = src.tagName.toLowerCase();
  const isCustom = tag.includes('-');
  const out = document.createElement(isCustom ? 'div' : tag) as HTMLElement;
  for (const attr of Array.from(src.attributes)) {
    if (attr.name === 'data-card-id' || attr.name === 'data-selected') {
      continue;
    }
    out.setAttribute(attr.name, attr.value);
  }
  const cs = getComputedStyle(src);
  for (const prop of CLONE_VISUAL_PROPS) {
    const value = cs[prop as keyof CSSStyleDeclaration];
    if (typeof value === 'string' && value.length > 0) {
      out.style.setProperty(toKebab(prop), value);
    }
  }
  for (const child of Array.from(src.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      out.appendChild(buildInertVisualClone(child as HTMLElement));
    } else if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(child.cloneNode(true));
    }
  }
  return out;
}

function toKebab(prop: string): string {
  return prop.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`);
}

function getElementVisualScale(el: HTMLElement, rect: DOMRect): number {
  const width = el.offsetWidth;
  if (!width || !rect.width) return 1;
  return rect.width / width;
}

function wheelDeltaToPixels(
  delta: number,
  deltaMode: number,
  pageSize: number
): number {
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return delta * 16;
  }
  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return delta * pageSize;
  }
  return delta;
}

function findKanbanHorizontalScrollTarget(
  eventTarget: EventTarget | null,
  root: HTMLElement
): HTMLElement | undefined {
  if (!(eventTarget instanceof Element)) {
    return undefined;
  }
  const target = eventTarget.closest<HTMLElement>(
    KANBAN_HORIZONTAL_SCROLL_TARGET_SELECTOR
  );
  if (target && root.contains(target)) {
    return target;
  }

  const row = eventTarget.closest<HTMLElement>('affine-data-view-kanban-row');
  const rowScroller = row?.querySelector<HTMLElement>(
    '.row-body-scroll[data-row-key]'
  );
  if (rowScroller && root.contains(rowScroller)) {
    return rowScroller;
  }

  return (
    root.querySelector<HTMLElement>('[data-kanban-canvas-x-scroll="flat"]') ??
    undefined
  );
}

function canScrollAxis(element: HTMLElement, axis: 'x' | 'y'): boolean {
  return axis === 'x'
    ? element.scrollWidth > element.clientWidth
    : element.scrollHeight > element.clientHeight;
}

function setScrollAxis(
  element: HTMLElement,
  axis: 'x' | 'y',
  delta: number
): void {
  if (axis === 'x') {
    element.scrollLeft += delta;
  } else {
    element.scrollTop += delta;
  }
}

export class KanbanViewUILogic extends DataViewUILogicBase<
  KanbanSingleView,
  KanbanViewSelectionWithType
> {
  ui$ = signal<KanbanViewUI | undefined>();
  clipboardController = new KanbanClipboardController(this);
  dragController = new KanbanDragController(this);
  hotkeysController = new KanbanHotkeysController(this);
  selectionController = new KanbanSelectionController(this);

  /**
   * View-agnostic live-preview card reorder. Shared with Gantt and
   * Table — Kanban's geometry is 2D (columns + cards), so the
   * `pointToTargetIndex` hit-tests the hovered group first and then
   * the card-slot inside it. Within the source column the dragged
   * cards extract (stationary cards compact up); in the target column
   * the cards shift down to open a slot. The dragged card(s) follow
   * the pointer via {@link RowReorderGhost} because cards fundamentally
   * move across space in Kanban — not in place like Table rows.
   */
  readonly reorder = new LivePreviewReorderController();

  /**
   * Shared click/Shift/Ctrl/Meta multi-select. Scope is the kanban
   * group key, so Shift-Click range-fills within one column. Drag of
   * any card in the active set moves the whole block.
   */
  readonly rowSelection = new RowSelectionController(
    this.buildRowSelectionHost()
  );

  private buildRowSelectionHost(): RowSelectionHost {
    return {
      getRowIdsInOrder: scope => {
        const groups = this.groups$.value ?? [];
        if (scope != null) {
          const g = groups.find(v => v?.key === scope);
          return (g?.rows ?? []).map(r => r.rowId);
        }
        const out: string[] = [];
        for (const g of groups) {
          for (const r of g?.rows ?? []) out.push(r.rowId);
        }
        return out;
      },
    };
  }

  /**
   * Resolve the group key for a card id. O(groups × cards) but called
   * at most once per card during a drag — the `groupDataMap$` is a
   * signal so repeated reads don't re-traverse the store.
   */
  findCardGroup(cardId: string): string | undefined {
    const map = this.view.groupTrait.groupDataMap$.value;
    if (!map) return undefined;
    for (const [key, group] of Object.entries(map)) {
      if (group?.rows.some(r => r.rowId === cardId)) return key;
    }
    return undefined;
  }

  /**
   * Compose the cell scope string used as a drop-target id. Matches
   * `captureKanbanRects`: `${rowKey}::${colKey}`. Kept as a single
   * method so the encoding can evolve in one place.
   */
  scopeOf(rowKey: string, colKey: string): string {
    return `${rowKey}::${colKey}`;
  }

  parseScope(scope: string): { rowKey: string; colKey: string } | undefined {
    const idx = scope.indexOf('::');
    if (idx < 0) return undefined;
    return {
      rowKey: scope.slice(0, idx),
      colKey: scope.slice(idx + 2),
    };
  }

  /**
   * Render-window sizes for the board axes. The policy constants are
   * the initial sample size — clicking an overflow tile widens the
   * window by one more policy step, so only what the user asked to
   * see is actually rendered. Board-wide (not per row) so every L2
   * swimlane shows the same column set. Transient view state: resets
   * on remount, never persisted.
   */
  readonly columnRenderLimit$ = signal(DATA_SOURCE_KANBAN_GROUP_RENDER_LIMIT);

  readonly rowGroupRenderLimit$ = signal(
    DATA_SOURCE_KANBAN_ROW_GROUP_RENDER_LIMIT
  );

  showMoreColumns = () => {
    this.columnRenderLimit$.value += DATA_SOURCE_KANBAN_GROUP_RENDER_LIMIT;
  };

  showMoreRowGroups = () => {
    this.rowGroupRenderLimit$.value +=
      DATA_SOURCE_KANBAN_ROW_GROUP_RENDER_LIMIT;
  };

  groupTrait$ = computed(() => {
    return this.view.traitGet(groupTraitKey);
  });

  group2Trait$ = computed(() => {
    return this.view.traitGet(group2TraitKey);
  });

  groups$ = computed(() => {
    const groupTrait = this.groupTrait$.value;
    return groupTrait?.groupsDataList$.value || [];
  });

  rows$ = computed(() => {
    const g2 = this.group2Trait$.value;
    return g2?.groupsDataList$.value || [];
  });

  /**
   * Raw 2-D group map keyed by rowKey. Consumers (KanbanGroup) intersect
   * this with the L1 column's rows to render cell contents. Exposed as
   * signal so downstream re-renders stay reactive.
   */
  group2Map$ = computed(() => {
    return this.group2Trait$.value?.groupDataMap$.value;
  });

  findCardRow(cardId: string): string | undefined {
    const map = this.group2Map$.value;
    if (!map) return undefined;
    for (const [key, group] of Object.entries(map)) {
      if (group?.rows.some(r => r.rowId === cardId)) return key;
    }
    return undefined;
  }

  private get readonly() {
    return this.view.readonly$.value;
  }

  clearSelection = () => {
    this.selectionController.clear();
  };

  addRow = (position: InsertToPosition) => {
    if (this.readonly) return;
    const rowId = this.view.rowAdd(position);
    if (rowId) {
      this.root.openDetailPanel({
        view: this.view,
        rowId,
      });
    }
    this.ui$.value?.requestUpdate();
    return rowId;
  };

  focusFirstCell = () => {
    this.selectionController.focusFirstCell();
  };

  showIndicator = (evt: MouseEvent) => {
    return this.dragController.showIndicator(evt, undefined) != null;
  };

  hideIndicator = () => {
    this.dragController.dropPreview.remove();
  };

  moveTo = (id: string, evt: MouseEvent) => {
    const position = this.dragController.getInsertPosition(evt);
    if (!position) return;
    const toCol = position.group.group.key;
    const toRow = position.group.rowKey;
    const fromCol = this.findCardGroup(id);
    const fromRow = this.findCardRow(id);
    this.view.groupTrait.moveCardTo(id, fromCol, toCol, position.position);
    if (fromRow !== toRow) {
      this.view.group2Trait.moveCardTo(id, fromRow, toRow, position.position);
    }
  };

  onWheel = (event: WheelEvent) => {
    if (event.metaKey || event.ctrlKey) {
      return;
    }

    const root = event.currentTarget;
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const horizontalTarget = findKanbanHorizontalScrollTarget(
      event.target,
      root
    );
    const deltaX = wheelDeltaToPixels(
      event.deltaX,
      event.deltaMode,
      horizontalTarget?.clientWidth ?? root.clientWidth
    );
    const deltaY = wheelDeltaToPixels(
      event.deltaY,
      event.deltaMode,
      root.clientHeight
    );
    const canScrollY = canScrollAxis(root, 'y');
    const canScrollX =
      !!horizontalTarget && canScrollAxis(horizontalTarget, 'x');

    if (event.shiftKey && canScrollX) {
      setScrollAxis(horizontalTarget, 'x', deltaY || deltaX);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!event.shiftKey && canScrollY && deltaY !== 0) {
      setScrollAxis(root, 'y', deltaY);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (canScrollX || canScrollY) {
      event.stopPropagation();
    }
  };

  /**
   * Inline icon+label button that opens the "create new group" input
   * for the given trait. Used for both axes of the 2-D board: L1
   * columns ("Add group", top-left) and L2 sub groups ("Add
   * subgroup", below the rows). Renders nothing when the trait's
   * grouping property type can't create groups (relation, date,
   * checkbox — only select-likes define `addGroup`).
   */
  renderAddGroupButton = (groupHelper: GroupTrait, label: string) => {
    const addGroup = groupHelper.addGroup;
    if (!addGroup) {
      return;
    }
    const readonly = this.view.readonly$.value;
    const add = (e: MouseEvent) => {
      if (readonly) {
        return;
      }
      const ele = e.currentTarget as HTMLElement;
      popAddGroupInput(
        popupTargetFromElement(ele),
        groupHelper,
        this.view.manager.dataSource
      );
    };
    return html` <div
      style="height: 32px;flex-shrink:0;display:flex;align-items:center;"
      aria-disabled="${readonly ? 'true' : 'false'}"
      @click="${readonly ? undefined : add}"
    >
      <div class="${addGroupIconStyle}" data-readonly="${readonly}">
        ${AddCursorIcon()}
        <span style="margin-left:4px;font-size:var(--data-view-cell-text-size);"
          >${label}</span
        >
      </div>
    </div>`;
  };

  scrollContainer$ = signal<HTMLElement | undefined>(undefined);

  /**
   * Entry point for card reorder. Snapshots every visible card's rect
   * per group so hit-test + live-shift run against frozen geometry —
   * re-measuring mid-drag would compound the applied `translateY` into
   * a runaway feedback loop. Adds the seed to the selection when it
   * wasn't already part of it so a plain-click-drag behaves like a
   * one-card move.
   */
  startCardReorder(
    seedCardId: string,
    evt: PointerEvent,
    captureTarget: HTMLElement
  ): void {
    const ui = this.ui$.value;
    if (!ui) return;
    const snapshot = ui.captureKanbanRects();
    if (snapshot.length === 0) return;
    const visualScale = ui.getVisualScale();

    const selected = this.rowSelection.selectedIds$.value;
    if (!selected.has(seedCardId)) {
      this.rowSelection.select(seedCardId, {});
    }
    const draggedIds = this.rowSelection.selectedIds$.value;
    const viewportSnapshot = ui.captureReorderViewport(draggedIds);
    ui.prepareReorderGhost(seedCardId, evt, captureTarget);
    ui.prepareDropPlaceholder(snapshot, draggedIds, visualScale);

    this.reorder.start({
      seedRowId: seedCardId,
      selectedIds: draggedIds,
      pointer: evt,
      geometry: buildKanbanReorderGeometry(snapshot, visualScale),
      host: this.reorderHost(),
      captureTarget,
      onCommit: () => ui.restoreReorderViewport(viewportSnapshot),
    });
  }

  private reorderHost(): ReorderHost {
    const view = this.view;
    return {
      getRowIdsInOrder: scope => {
        if (scope == null) {
          const out: string[] = [];
          const rows = this.rows$.value ?? [];
          const hasRowGroups = rows.length > 0;
          if (!hasRowGroups) {
            for (const col of this.groups$.value ?? []) {
              if (!col) continue;
              out.push(...col.rows.map(r => r.rowId));
            }
            return out;
          }
          for (const row of rows) {
            if (!row) continue;
            const rowSet = new Set(row.rows.map(r => r.rowId));
            for (const col of this.groups$.value ?? []) {
              if (!col) continue;
              out.push(
                ...col.rows.filter(r => rowSet.has(r.rowId)).map(r => r.rowId)
              );
            }
          }
          return out;
        }
        const parsed = this.parseScope(scope);
        if (!parsed) return [];
        const col = view.groupTrait.groupDataMap$.value?.[parsed.colKey];
        if (!col) return [];
        const rowMap = view.group2Trait.groupDataMap$.value;
        if (!rowMap || parsed.rowKey === '') {
          return col.rows.map(r => r.rowId);
        }
        const row = rowMap[parsed.rowKey];
        if (!row) return [];
        const rowSet = new Set(row.rows.map(r => r.rowId));
        return col.rows.filter(r => rowSet.has(r.rowId)).map(r => r.rowId);
      },
      getRowTitles: ids => this.readCardTitles(ids),
      moveRows: (cardIds, target, toScope) => {
        if (!toScope) return;
        const parsed = this.parseScope(toScope);
        if (!parsed) return;
        const { rowKey: toRow, colKey: toCol } = parsed;
        const rowMap = view.group2Trait.groupDataMap$.value;
        const hasRowGroups = !!rowMap && toRow !== '';
        // Re-sort the dragged set by its current on-screen order so
        // batch drops preserve relative order after the N moveCardTo
        // calls against a single insertion anchor.
        const orderedSet = new Set(cardIds);
        const allOrdered: string[] = [];
        const rows = this.rows$.value ?? [];
        if (!hasRowGroups || rows.length === 0) {
          for (const col of this.groups$.value ?? []) {
            if (!col) continue;
            for (const r of col.rows) {
              if (orderedSet.has(r.rowId)) allOrdered.push(r.rowId);
            }
          }
        } else {
          for (const row of rows) {
            if (!row) continue;
            const rowSet = new Set(row.rows.map(r => r.rowId));
            for (const col of this.groups$.value ?? []) {
              if (!col) continue;
              for (const r of col.rows) {
                if (rowSet.has(r.rowId) && orderedSet.has(r.rowId)) {
                  allOrdered.push(r.rowId);
                }
              }
            }
          }
        }
        for (let i = allOrdered.length - 1; i >= 0; i--) {
          const cardId = allOrdered[i]!;
          const fromCol = this.findCardGroup(cardId);
          const fromRow = this.findCardRow(cardId);
          // L1 (column) write. Re-sort within the target column.
          view.groupTrait.moveCardTo(cardId, fromCol, toCol, target);
          // L2 (row) write only when the row changed — the sort
          // anchor is the same target card, which lives in the new
          // cell after the L1 write. Calling moveCardTo on the row
          // trait shuffles row-internal manuallyCardSort too, which
          // is fine because the row is the full swimlane.
          if (hasRowGroups && fromRow !== toRow) {
            view.group2Trait.moveCardTo(cardId, fromRow, toRow, target);
          }
        }
      },
    };
  }

  readCardTitles(ids: readonly string[]): string[] {
    const view = this.view;
    const titleCol = view.mainProperties$.value.titleColumn;
    return ids.map(id => {
      if (!titleCol) return 'Untitled';
      return (
        view.cellGetOrCreate(id, titleCol).stringValue$.value || 'Untitled'
      );
    });
  }

  renderer = createUniComponentFromWebComponent(KanbanViewUI);
}

/**
 * Build a per-drag `ReorderGeometry` that reads only from the frozen
 * pointer-down snapshot. `pointToTargetIndex` walks groups by X first
 * (stable column layout), then cards by Y; `getLiveShift` dispatches
 * per-group through {@link computeGroupedPostDropShifts}. The inline
 * preview only moves stationary cards in the source/same scope:
 * source groups compact and same-scope reorders shift in place, while
 * foreign target groups get a real DOM placeholder from the Kanban
 * group renderer. That placeholder expands the target column height;
 * applying insertion transforms there as well would double-open the
 * slot.
 */
function buildKanbanReorderGeometry(
  snapshot: KanbanLayoutSnapshot,
  visualScale = 1
): ReorderGeometry {
  const scale = visualScale || 1;
  const itemsByScope: { [scope: string]: readonly ReorderShiftItem[] } = {};
  const heightById = new Map<string, number>();
  for (const group of snapshot) {
    const items: ReorderShiftItem[] = [];
    for (const card of group.cards) {
      // Include the gap so running-top diffs match the actual pixels
      // between cards — `group-body` uses `gap: 12px`, which the live
      // DOM applies between siblings.
      const slot = card.height / scale + KANBAN_CARD_GAP;
      items.push({ rowId: card.cardId, height: slot });
      heightById.set(card.cardId, slot);
    }
    itemsByScope[group.scope] = items;
  }

  return {
    pointToTargetIndex: (clientX, clientY) => {
      for (const group of snapshot) {
        if (
          clientX < group.left ||
          clientX > group.right ||
          clientY < group.top ||
          clientY > group.bottom
        )
          continue;
        const cards = group.cards;
        if (cards.length === 0) return { index: 0, scope: group.scope };
        const first = cards[0]!;
        const last = cards[cards.length - 1]!;
        if (clientY < first.top) return { index: 0, scope: group.scope };
        if (clientY > last.bottom) {
          return { index: cards.length, scope: group.scope };
        }
        for (let i = 0; i < cards.length; i++) {
          const r = cards[i]!;
          if (clientY <= r.bottom) {
            const mid = (r.top + r.bottom) / 2;
            return {
              index: clientY < mid ? i : i + 1,
              scope: group.scope,
            };
          }
        }
      }
      return null;
    },
    getLiveShift: state => {
      if (state.targetIndex === null) {
        return new Map();
      }
      let blockHeight = 0;
      for (const id of state.draggedIds) {
        blockHeight += heightById.get(id) ?? 0;
      }
      const shifts = new Map(
        computeGroupedPostDropShifts(itemsByScope, state, blockHeight)
      );
      const targetItems =
        state.dropScope == null ? undefined : itemsByScope[state.dropScope];
      const targetContainsDragged = targetItems?.some(item =>
        state.draggedIds.has(item.rowId)
      );
      if (targetItems && !targetContainsDragged) {
        for (const item of targetItems) {
          shifts.delete(item.rowId);
        }
      }

      for (const id of state.draggedIds) {
        shifts.delete(id);
      }
      return shifts;
    },
  };
}

export class KanbanViewUI extends DataViewUIBase<KanbanViewUILogic> {
  private readonly canvasViewport = new CanvasViewportParticipantController(
    this,
    'kanban',
    () => this.logic.view.featureFlags$.value.use_shared_canvas_viewport
  );

  /**
   * Floating drag preview that tracks the cursor while a card reorder
   * is active. A transparent DOM replica of the dragged card(s),
   * stacked, positioned next to the cursor. Mounted under
   * `document.body` so `position: fixed` resolves against the
   * viewport even when the kanban is rendered inside a transformed
   * Edgeless ancestor. Inline cards still glide to their post-drop
   * slot via `liveShifts$` — this ghost is the second preview the
   * user sees attached to the cursor.
   */
  private ghostContainer: HTMLDivElement | null = null;

  private lastGhostDraggedIds: ReadonlySet<string> | null = null;

  private readonly flatXScrollLeft$ = signal(0);
  private scrollStateCleanup: (() => void) | undefined;
  private reorderViewportRestoreFrame: number | undefined;

  /**
   * Pointer offset within the seed card at drag start. The ghost
   * root is anchored so the cursor stays at the exact same relative
   * point in the ghost as it was in the live card when the user
   * pressed down — no bottom-right drift, no mid-card snap.
   */
  private ghostSeedOffsetX = 0;
  private ghostSeedOffsetY = 0;
  private ghostVisualScale = 1;
  private ghostSeedCardId: string | null = null;
  private dropPlaceholderHeight = 0;

  readonly sortContext = createSortContext({
    activators: defaultActivators,
    container: this,
    disabled: computed(
      () => this.logic.groupTrait$.value?.isExplicitSort$.value ?? false
    ),
    resolveNode: (_id, nodes, activeNode) => {
      const activeLane = activeNode.parentElement;
      return (
        nodes.find(node => node === activeNode) ??
        nodes.find(node => node.parentElement === activeLane) ??
        nodes[0]
      );
    },
    createOverlay: active => {
      const scale = this.sortContext.scale$.value;
      const offsetParentRect = this.offsetParent?.getBoundingClientRect();
      if (!offsetParentRect) return;

      const preview = document.createElement('div');
      preview.classList.add('data-view-drag-preview');
      Object.assign(preview.style, {
        position: 'absolute',
        zIndex: '999',
        pointerEvents: 'none',
        opacity: '0.65',
        overflow: 'hidden',
        maxHeight: 'min(520px, 80vh)',
        width: `${active.rect.width / scale.x}px`,
        top: `${(active.rect.top - offsetParentRect.top) / scale.y}px`,
        left: `${(active.rect.left - offsetParentRect.left) / scale.x}px`,
        borderRadius: '8px',
        boxShadow: 'var(--streamlain-shadow-2)',
      });
      const mirrors = Array.from(
        this.querySelectorAll<HTMLElement>('affine-data-view-kanban-group')
      ).filter(node => node.dataset['key'] === active.id);
      const visibleMirrors = mirrors.filter(node => {
        const rowBody = node.closest<HTMLElement>(
          '.row-body-scroll[data-row-key]'
        );
        return (
          !rowBody ||
          (rowBody.dataset['collapsed'] !== 'true' &&
            rowBody.getAttribute('aria-hidden') !== 'true')
        );
      });
      let previewSources: HTMLElement[] = [active.node];

      // Flat Kanban splits one logical column into a sticky header and a
      // scrollable body mirror. L2 Kanban renders one cell mirror per visible
      // row. Compose the complete visible logical column in both cases.
      if (active.node.hasAttribute('data-header-only')) {
        const bodyMirror = Array.from(
          this.querySelectorAll<HTMLElement>('affine-data-view-kanban-group')
        ).find(
          node =>
            node.dataset['key'] === active.id &&
            !node.hasAttribute('data-header-only')
        );
        if (bodyMirror) {
          previewSources = [active.node, bodyMirror];
        }
      } else if (active.node.hasAttribute('data-row-key')) {
        previewSources = visibleMirrors;
      }
      previewSources.forEach(source => {
        preview.append(buildInertVisualClone(source));
      });

      // The inert visual clone must never re-register itself as a sortable
      // participant when it is mounted under the DnD container.
      const previewNodes = [
        preview,
        ...preview.querySelectorAll<HTMLElement>(
          '[data-wc-dnd-draggable-id], [data-wc-dnd-droppable-id], [data-wc-dnd-drag-handler-id]'
        ),
      ];
      previewNodes.forEach(node => {
        node.removeAttribute('data-wc-dnd-draggable-id');
        node.removeAttribute('data-wc-dnd-droppable-id');
        node.removeAttribute('data-wc-dnd-drag-handler-id');
      });

      const previousOpacity = mirrors.map(node => node.style.opacity);
      mirrors.forEach(node => {
        node.style.opacity = '0.12';
      });
      this.append(preview);

      return {
        overlay: preview,
        cleanup: () => {
          preview.remove();
          mirrors.forEach((node, index) => {
            node.style.opacity = previousOpacity[index] ?? '';
          });
        },
      };
    },
    onDragEnd: evt => {
      if (this.logic.view.readonly$.value) return;
      const over = evt.over;
      const activeId = evt.active.id;
      const groupTrait = this.logic.groupTrait$.value;
      const groups = groupTrait?.groupsDataList$.value;
      if (over && over.id !== activeId && groups) {
        const activeIndex = groups.findIndex(data => data?.key === activeId);
        const overIndex = groups.findIndex(data => data?.key === over.id);

        if (activeIndex < 0 || overIndex < 0) return;

        groupTrait?.moveGroupTo(
          activeId,
          activeIndex > overIndex
            ? {
                before: true,
                id: over.id,
              }
            : {
                before: false,
                id: over.id,
              }
        );
      }
    },
    modifiers: [
      ({ transform }) => {
        return {
          ...transform,
          y: 0,
        };
      },
    ],
    items: computed(() => {
      return this.logic.groups$.value?.map(v => v?.key ?? 'default key') ?? [];
    }),
    strategy: horizontalListSortingStrategy,
  });

  readonly rowSortContext = createSortContext({
    activators: defaultActivators,
    container: this,
    disabled: computed(
      () =>
        this.logic.view.readonly$.value ||
        (this.logic.group2Trait$.value?.isExplicitSort$.value ?? false)
    ),
    createOverlay: active => {
      const scale = this.rowSortContext.scale$.value;
      const offsetParentRect = this.offsetParent?.getBoundingClientRect();
      if (!offsetParentRect) return;

      const preview = buildInertVisualClone(active.node);
      preview.classList.add('data-view-drag-preview');
      Object.assign(preview.style, {
        position: 'absolute',
        zIndex: '999',
        pointerEvents: 'none',
        opacity: '0.72',
        overflow: 'hidden',
        maxHeight: 'min(520px, 80vh)',
        width: `${active.rect.width / scale.x}px`,
        top: `${(active.rect.top - offsetParentRect.top) / scale.y}px`,
        left: `${(active.rect.left - offsetParentRect.left) / scale.x}px`,
        borderRadius: '8px',
        boxShadow: 'var(--streamlain-shadow-2)',
      });
      [
        preview,
        ...preview.querySelectorAll<HTMLElement>(
          '[data-wc-dnd-draggable-id], [data-wc-dnd-droppable-id], [data-wc-dnd-drag-handler-id]'
        ),
      ].forEach(node => {
        node.removeAttribute('data-wc-dnd-draggable-id');
        node.removeAttribute('data-wc-dnd-droppable-id');
        node.removeAttribute('data-wc-dnd-drag-handler-id');
      });

      const previousOpacity = active.node.style.opacity;
      active.node.style.opacity = '0.12';
      this.append(preview);
      return {
        overlay: preview,
        cleanup: () => {
          preview.remove();
          active.node.style.opacity = previousOpacity;
        },
      };
    },
    onDragEnd: event => {
      if (this.logic.view.readonly$.value) return;
      const over = event.over;
      const activeKey = kanbanRowKeyFromSortId(String(event.active.id));
      const overKey = over
        ? kanbanRowKeyFromSortId(String(over.id))
        : undefined;
      const group2Trait = this.logic.group2Trait$.value;
      const rows = group2Trait?.groupsDataList$.value;
      if (!activeKey || !overKey || activeKey === overKey || !rows) return;

      const activeIndex = rows.findIndex(row => row?.key === activeKey);
      const overIndex = rows.findIndex(row => row?.key === overKey);
      if (activeIndex < 0 || overIndex < 0) return;

      group2Trait.moveGroupTo(
        activeKey,
        activeIndex > overIndex
          ? { before: true, id: overKey }
          : { before: false, id: overKey }
      );
    },
    modifiers: [({ transform }) => ({ ...transform, x: 0 })],
    items: computed(() => {
      const { visibleGroups } = resolveKanbanVisibleGroups(
        this.logic.rows$.value.filter(
          (row): row is NonNullable<typeof row> => row != null
        ),
        this.logic.rowGroupRenderLimit$.value
      );
      return visibleGroups.map(row => kanbanRowSortId(row.key));
    }),
    strategy: verticalListSortingStrategy,
  });

  private renderAddGroupColumn() {
    const groupTrait = this.logic.groupTrait$.value;
    if (!groupTrait?.addGroup) return html``;

    const readonly = this.logic.view.readonly$.value;
    const add = (e: MouseEvent) => {
      if (readonly) {
        return;
      }
      const ele = e.currentTarget as HTMLElement;
      popAddGroupInput(
        popupTargetFromElement(ele),
        groupTrait,
        this.logic.view.manager.dataSource
      );
    };

    return html`<div
      class="${addGroupColumnStyle}"
      title="Add column"
      aria-disabled="${readonly ? 'true' : 'false'}"
      data-readonly="${readonly}"
      @click="${readonly ? undefined : add}"
    >
      ${AddCursorIcon()}
    </div>`;
  }

  private readonly onFlatHorizontalScroll = (event: Event) => {
    const target = event.currentTarget;
    if (target instanceof HTMLElement) {
      this.flatXScrollLeft$.value = target.scrollLeft;
    }
  };

  private readonly syncVerticalScrollStyleState = () => {
    const scrollTop = this.logic.scrollContainer$.value?.scrollTop ?? 0;
    this.dataset['kanbanYScrolled'] = scrollTop > 0 ? 'true' : 'false';
  };

  private renderRows() {
    const rows = this.logic.rows$.value ?? [];
    const safeRows = rows.filter(
      (row): row is NonNullable<(typeof rows)[number]> => row != null
    );

    if (safeRows.length === 0) {
      // No L2 groupBy — flat 1D layout: groups as horizontal columns.
      // Add-column button is the leftmost element (narrow ghost column).
      const groups = (this.logic.groups$.value ?? []).filter(
        (col): col is NonNullable<typeof col> => col != null
      );
      const { visibleGroups, hiddenCount } = resolveKanbanVisibleGroups(
        groups,
        this.logic.columnRenderLimit$.value
      );
      const stickyHeaderInnerStyle = styleMap({
        transform: `translateX(${-this.flatXScrollLeft$.value}px)`,
      });

      return html`<div class="${kanbanFlatOuterStyle}">
        ${this.renderAddGroupColumn()}
        <div class="${kanbanFlatColumnsStyle}">
          <div class="${kanbanFlatStickyHeaderStyle}">
            <div
              class="${kanbanFlatStickyHeaderInnerStyle}"
              style="${stickyHeaderInnerStyle}"
            >
              ${visibleGroups.map(
                col =>
                  html`<affine-data-view-kanban-group
                    ${sortable(col.key)}
                    data-key="${col.key}"
                    data-row-key=""
                    .headerOnly="${true}"
                    .rowKey="${''}"
                    .kanbanViewLogic="${this.logic}"
                    .group="${col}"
                  ></affine-data-view-kanban-group>`
              )}
              ${hiddenCount > 0
                ? html`<div
                    class="${kanbanOverflowTileStyle}"
                    style="background:var(--streamlain-background-primary-color);"
                    role="button"
                    title="${dataViewI18nT('kanban.showMoreColumnsTitle')}"
                    data-hidden-kanban-group-count="${hiddenCount}"
                    @click="${this.logic.showMoreColumns}"
                  >
                    ${dataViewI18nT('kanban.showMoreColumns', {
                      count: hiddenCount,
                    })}
                  </div>`
                : ''}
            </div>
          </div>
          <div
            data-kanban-canvas-x-scroll="flat"
            class="${kanbanFlatScrollerStyle}"
            @scroll="${this.onFlatHorizontalScroll}"
          >
            ${visibleGroups.map(
              col =>
                html`<affine-data-view-kanban-group
                  ${sortable(col.key)}
                  data-key="${col.key}"
                  data-row-key=""
                  .hideHeader="${true}"
                  .rowKey="${''}"
                  .kanbanViewLogic="${this.logic}"
                  .group="${col}"
                ></affine-data-view-kanban-group>`
            )}
            ${hiddenCount > 0
              ? html`<div
                  class="${kanbanOverflowTileStyle}"
                  role="button"
                  title="${dataViewI18nT('kanban.showMoreColumnsTitle')}"
                  data-hidden-kanban-group-count="${hiddenCount}"
                  @click="${this.logic.showMoreColumns}"
                >
                  ${dataViewI18nT('kanban.showMoreColumns', {
                    count: hiddenCount,
                  })}
                </div>`
              : ''}
          </div>
        </div>
      </div>`;
    }

    const groupTrait = this.logic.groupTrait$.value;
    const group2Trait = this.logic.group2Trait$.value;
    const { visibleGroups: visibleRows, hiddenCount } =
      resolveKanbanVisibleGroups(
        safeRows,
        this.logic.rowGroupRenderLimit$.value
      );
    // 2-D board chrome: L1 column creation sits top-left (columns are
    // the horizontal axis), L2 sub group creation sits below the row
    // list (rows are the vertical axis). Each button only renders when
    // its grouping property type supports creating groups.
    return html`${groupTrait
      ? this.logic.renderAddGroupButton(
          groupTrait,
          dataViewI18nT('kanban.addGroup')
        )
      : ''}${visibleRows.map(row => {
      return html`<affine-data-view-kanban-row
        ${sortable(kanbanRowSortId(row.key))}
        data-row-key="${row.key}"
        .row="${row}"
        .kanbanViewLogic="${this.logic}"
      ></affine-data-view-kanban-row>`;
    })}${hiddenCount > 0
      ? html`<div
          class="${kanbanOverflowTileStyle}"
          style="width:auto;"
          role="button"
          title="${dataViewI18nT('kanban.showMoreRowGroupsTitle')}"
          data-hidden-kanban-row-group-count="${hiddenCount}"
          @click="${this.logic.showMoreRowGroups}"
        >
          ${dataViewI18nT('kanban.showMoreRowGroups', { count: hiddenCount })}
        </div>`
      : ''}${group2Trait
      ? this.logic.renderAddGroupButton(
          group2Trait,
          dataViewI18nT('kanban.addSubgroup')
        )
      : ''}`;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.logic.ui$.value = this;
    this.disposables.add(
      effect(() => {
        this.style.setProperty(
          '--kanban-column-width',
          `${this.logic.view.columnWidth$.value}px`
        );
      })
    );
    this.logic.clipboardController.hostConnected();
    this.logic.dragController.hostConnected();
    this.logic.hotkeysController.hostConnected();
    this.logic.selectionController.hostConnected();
    this.classList.add('kanban-view', kanbanViewStyle);
    this.style.userSelect = 'none';
    this.style.display = 'flex';
    this.style.flexDirection = 'column';

    // Surface the Shift key so CSS can swap the card cursor to a
    // range-select hint while the modifier is held. Mirrors Table and
    // Gantt.
    this.disposables.add(
      effect(() => {
        const active = getShiftKeyActive$().value;
        if (active) this.dataset['shiftActive'] = 'true';
        else delete this.dataset['shiftActive'];
      })
    );

    // Surface the live-reorder state so CSS can relax the dedicated
    // horizontal viewport during a card drag. Keeping scrolling on a wrapper
    // prevents horizontal overflow from turning the lane content itself into
    // an implicit vertical scroll container.
    this.disposables.add(
      effect(() => {
        const dragging = this.logic.reorder.state$.value != null;
        if (dragging) this.dataset['reorderActive'] = 'true';
        else delete this.dataset['reorderActive'];
      })
    );
    this.disposables.add(
      effect(() => {
        this.scrollStateCleanup?.();
        this.scrollStateCleanup = undefined;
        const container = this.logic.scrollContainer$.value;
        this.syncVerticalScrollStyleState();
        if (!container) {
          return;
        }
        container.addEventListener(
          'scroll',
          this.syncVerticalScrollStyleState,
          {
            passive: true,
          }
        );
        this.scrollStateCleanup = () => {
          container.removeEventListener(
            'scroll',
            this.syncVerticalScrollStyleState
          );
        };
      })
    );
    this.disposables.add({ dispose: () => this.scrollStateCleanup?.() });

    // Auto-scroll while dragging: when the ghost cursor gets within
    // `EDGE_THRESHOLD` of the kanban scroll container's top/bottom
    // edge, start a rAF loop that pans the container toward the
    // pointer. Speed scales linearly with the edge penetration depth
    // so the pan feels proportional — a pointer right at the edge
    // moves at `MAX_SPEED_PX`/frame, a pointer just inside the
    // threshold inches along. No horizontal pan (kanban columns are
    // horizontally scrolled inside the row's own overflow-x:auto,
    // not on the outer container). The effect owns the loop lifecycle
    // so teardown on drag-end cancels any pending rAF cleanly.
    let autoScrollRaf = 0;
    const stopAutoScroll = () => {
      if (autoScrollRaf) {
        cancelAnimationFrame(autoScrollRaf);
        autoScrollRaf = 0;
      }
    };
    this.disposables.add(stopAutoScroll);
    this.disposables.add(
      effect(() => {
        const dragging = this.logic.reorder.state$.value != null;
        if (!dragging) {
          stopAutoScroll();
          return;
        }
        const EDGE_THRESHOLD = 100;
        const MAX_SPEED_PX = 10;
        // Find the nearest vertically-scrollable ancestor. The kanban's
        // own kanbanRowsStyle scroll container sizes to its content in
        // most layouts (no fixed height), so its scrollHeight equals
        // clientHeight — panning it would be a no-op. The real vertical
        // scroll usually lives on a grandparent (document viewer, main
        // layout container). Walk up from the kanban, resolve the first
        // element whose computed `overflow-y` allows scrolling AND
        // whose scrollHeight exceeds clientHeight. Falls back to the
        // kanban's own scrollContainer when nothing else qualifies.
        const findScrollable = (from: HTMLElement): HTMLElement => {
          let el: HTMLElement | null = from;
          while (el) {
            if (el.scrollHeight > el.clientHeight) {
              const oy = getComputedStyle(el).overflowY;
              if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
                return el;
              }
            }
            el = el.parentElement;
          }
          return document.scrollingElement as HTMLElement;
        };
        const tick = () => {
          const ghost = this.logic.reorder.ghostPosition$.peek();
          const state = this.logic.reorder.state$.peek();
          if (!ghost || !state) {
            autoScrollRaf = 0;
            return;
          }
          // Resolve each frame so the right container is used even if
          // the kanban itself is re-parented mid-drag (unlikely but
          // cheap to guard). Fallback order: local scrollContainer if
          // it can actually scroll, else nearest scrollable ancestor.
          const localContainer = this.logic.scrollContainer$.value;
          const container =
            localContainer &&
            localContainer.scrollHeight > localContainer.clientHeight
              ? localContainer
              : findScrollable(this);
          if (!container) {
            autoScrollRaf = requestAnimationFrame(tick);
            return;
          }
          const rect = container.getBoundingClientRect();
          const topGap = ghost.clientY - rect.top;
          const bottomGap = rect.bottom - ghost.clientY;
          // Cubic easing on the penetration depth: 0 at the threshold,
          // ramps up gently toward the edge, full speed at/past the
          // boundary. Linear scaling felt too abrupt — the pointer
          // needed to be within ~10 px of the boundary to land a
          // moderate speed, then jumped to max the instant it crossed
          // threshold. Cubic gives the user a wide "creep" zone
          // (first 40 %) where scroll is barely-there, a middle band
          // (40-80 %) where it accelerates, and the final 20 % hits
          // max. Clamp depth to [0, 1] so ghost outside the container
          // keeps max speed without overshooting.
          const easeIn = (t: number) => {
            const clamped = Math.max(0, Math.min(1, t));
            return clamped * clamped * clamped;
          };
          let delta = 0;
          if (topGap < EDGE_THRESHOLD) {
            const depth = (EDGE_THRESHOLD - topGap) / EDGE_THRESHOLD;
            delta = -MAX_SPEED_PX * easeIn(depth);
          } else if (bottomGap < EDGE_THRESHOLD) {
            const depth = (EDGE_THRESHOLD - bottomGap) / EDGE_THRESHOLD;
            delta = MAX_SPEED_PX * easeIn(depth);
          }
          if (delta !== 0) {
            const max = container.scrollHeight - container.clientHeight;
            container.scrollTop = Math.max(
              0,
              Math.min(max, container.scrollTop + delta)
            );
          }
          autoScrollRaf = requestAnimationFrame(tick);
        };
        autoScrollRaf = requestAnimationFrame(tick);
      })
    );

    // Hover-to-expand on collapsed L2 rows during a drag. Lingering
    // over a collapsed row header for 350 ms expands it so the user
    // can drop inside — standard Trello / Linear affordance. Cancels
    // on leave so accidental fly-throughs don't flip lanes open. The
    // expand then triggers a re-render; the next pointer move rebuilds
    // the reorder geometry via the row's newly-visible groups.
    let hoverRowKey: string | null = null;
    let hoverStartedAt = 0;
    let hoverRaf = 0;
    const stopHoverExpand = () => {
      if (hoverRaf) {
        cancelAnimationFrame(hoverRaf);
        hoverRaf = 0;
      }
      hoverRowKey = null;
      hoverStartedAt = 0;
    };
    this.disposables.add(stopHoverExpand);
    this.disposables.add(
      effect(() => {
        const dragging = this.logic.reorder.state$.value != null;
        if (!dragging) {
          stopHoverExpand();
          return;
        }
        const HOVER_EXPAND_MS = 350;
        const tick = () => {
          const ghost = this.logic.reorder.ghostPosition$.peek();
          if (!ghost) {
            hoverRaf = 0;
            return;
          }
          const headers = this.querySelectorAll<HTMLElement>(
            '.row-header[data-collapsed="true"]'
          );
          let overKey: string | null = null;
          for (const header of headers) {
            const rect = header.getBoundingClientRect();
            if (
              ghost.clientX >= rect.left &&
              ghost.clientX <= rect.right &&
              ghost.clientY >= rect.top &&
              ghost.clientY <= rect.bottom
            ) {
              overKey = header.dataset['rowKey'] ?? null;
              break;
            }
          }
          if (overKey !== hoverRowKey) {
            hoverRowKey = overKey;
            hoverStartedAt = overKey ? performance.now() : 0;
          } else if (
            overKey &&
            hoverStartedAt &&
            performance.now() - hoverStartedAt >= HOVER_EXPAND_MS
          ) {
            this.logic.view.toggleRow2Collapsed(overKey);
            hoverRowKey = null;
            hoverStartedAt = 0;
          }
          hoverRaf = requestAnimationFrame(tick);
        };
        hoverRaf = requestAnimationFrame(tick);
      })
    );

    // Cursor-attached drag preview. Transparent DOM replicas of the
    // dragged card(s) at their ORIGINAL on-screen positions, relative
    // to the card the user grabbed — so moving the cursor slides the
    // whole set rigidly, with the cursor staying exactly where it
    // was inside the seed card when the drag started. Inline cards
    // still glide to their post-drop slot via `liveShifts$`; this
    // ghost is the second, cursor-tracking preview.
    const ghostRoot = document.createElement('div');
    ghostRoot.setAttribute('data-kanban-ghost', '');
    // Tag with the shared drag-preview class so the blue-white theme
    // CSS override block (.data-view-drag-preview *) picks up and
    // flips the V2 layer tokens to light, which is what the card
    // clones read for their background. Without this the ghost
    // renders transparent on top of the dark page body.
    ghostRoot.classList.add('data-view-drag-preview');
    Object.assign(ghostRoot.style, {
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: '9999',
      // Semi-transparent preview (50%) — signals "this is a ghost,
      // not the real card" while keeping the content readable. The
      // underlying clones have a concrete theme bg (see the
      // `data-selected` strip in the clone loop below), so even at
      // 50% the drag preview still reads as a solid card over the
      // board surface.
      opacity: '0.5',
      display: 'none',
      width: '0',
      height: '0',
      willChange: 'left, top',
    });
    document.body.appendChild(ghostRoot);
    this.ghostContainer = ghostRoot;
    this.disposables.add(
      effect(() => {
        const state = this.logic.reorder.state$.value;
        const pos = this.logic.reorder.ghostPosition$.value;
        if (!state || !pos) {
          if (this.lastGhostDraggedIds !== null) {
            ghostRoot.textContent = '';
            this.lastGhostDraggedIds = null;
          }
          ghostRoot.style.display = 'none';
          return;
        }

        // Rebuild clones only when the dragged-set identity changes —
        // pointermove fires at display framerate and the per-card DOM
        // walk + getComputedStyle is expensive.
        if (state.draggedIds !== this.lastGhostDraggedIds) {
          ghostRoot.textContent = '';
          const cardEls: {
            id: string;
            el: HTMLElement;
            layoutHeight: number;
            layoutWidth: number;
            rect: DOMRect;
          }[] = [];
          const groupEls = this.querySelectorAll<HTMLElement>(
            'affine-data-view-kanban-group'
          );
          for (const group of groupEls) {
            const cards = group.querySelectorAll<HTMLElement>(
              'affine-data-view-kanban-card'
            );
            for (const card of cards) {
              const id = card.dataset['cardId'];
              if (!id || !state.draggedIds.has(id)) continue;
              const rect = card.getBoundingClientRect();
              cardEls.push({
                id,
                el: card,
                layoutHeight: card.offsetHeight || rect.height,
                layoutWidth: card.offsetWidth || rect.width,
                rect,
              });
            }
          }
          // Seed = the card grabbed on pointerdown. Do not infer it
          // from the current ghost position: in Edgeless the first
          // clone rebuild can happen after the pointer has already
          // crossed into a target column, and deriving the grab offset
          // from that later position anchors the ghost halfway over
          // the board instead of under the cursor.
          const seed =
            cardEls.find(c => c.id === this.ghostSeedCardId) ?? cardEls[0];
          if (!seed) {
            this.lastGhostDraggedIds = state.draggedIds;
            return;
          }
          this.ghostVisualScale = getElementVisualScale(seed.el, seed.rect);
          ghostRoot.style.transformOrigin = '0 0';
          ghostRoot.style.transform = `scale(${this.ghostVisualScale})`;
          ghostRoot.style.width = `${seed.layoutWidth}px`;
          ghostRoot.style.height = `${seed.layoutHeight}px`;
          // Multi-select: stack every dragged card as a vertical
          // pile under the cursor. Seed goes on top, remaining
          // cards peek out with a small fanned offset so the user
          // can see the count. Without this the clones kept their
          // source positions — a multi-select drag spread across
          // columns scattered the ghosts back to where the cards
          // already live, which isn't a preview at all.
          // `STACK_FAN_PX` is the amount of the previous card left
          // visible under each new clone; `STACK_X_PX` adds a tiny
          // horizontal drift so nested cards are hinted at even
          // when vertical overflow clips.
          const STACK_FAN_PX = 12;
          const STACK_X_PX = 4;
          const orderedClones = [seed, ...cardEls.filter(c => c !== seed)];
          orderedClones.forEach((c, i) => {
            // Strip `data-selected` BEFORE reading computed styles.
            // The card's drag-select CSS paints `background-color:
            // primary-color-04` (4% alpha blue) over the normal
            // surface; `buildInertVisualClone` would otherwise
            // freeze that near-transparent color onto the clone as
            // an inline style and the ghost renders "without a
            // background". Synchronous remove → clone → restore
            // keeps the source visually stable (no Lit re-render
            // tick between calls).
            const wasSelected = c.el.hasAttribute('data-selected');
            if (wasSelected) c.el.removeAttribute('data-selected');
            const clone = buildInertVisualClone(c.el);
            if (wasSelected) c.el.setAttribute('data-selected', '');
            Object.assign(clone.style, {
              position: 'absolute',
              left: `${i * STACK_X_PX}px`,
              top: `${i * STACK_FAN_PX}px`,
              width: `${seed.layoutWidth}px`,
              height: `${seed.layoutHeight}px`,
              transform: 'none',
              transition: 'none',
              margin: '0',
              // Explicit z-index so the seed (i=0) sits on top of
              // the fanned stack — without it DOM order would put
              // later clones above the seed and the grab card
              // would be visually obscured.
              zIndex: `${orderedClones.length - i}`,
            });
            clone.style.setProperty('opacity', '1', 'important');
            ghostRoot.appendChild(clone);
          });
          this.lastGhostDraggedIds = state.draggedIds;
        }
        ghostRoot.style.display = 'block';
        ghostRoot.style.left = `${pos.clientX - this.ghostSeedOffsetX}px`;
        ghostRoot.style.top = `${pos.clientY - this.ghostSeedOffsetY}px`;
      })
    );

    // Bidirectional sync between the shared `rowSelection` (owns the
    // Click/Shift/Ctrl semantics) and the existing Kanban view
    // selection store (read by card ops, context menu, clipboard).
    // Id-equality guards stop write cycles. Cell focus is treated as
    // a REFINEMENT of a single-row selection: we keep the cell type
    // on the selectionController so cell editing and keyboard nav
    // still work, while rowSelection continues to hold the host card
    // so the visible highlight survives.
    this.disposables.add(
      effect(() => {
        const ids = this.logic.rowSelection.selectedIds$.value;
        const sel = this.logic.selectionController.selection;

        // Cell selection on an already-single-selected card is user-
        // initiated cell focus — don't overwrite it with a card-type
        // selection in the sync. Only collapse to `{type:card}` when
        // the rowSelection actually widened past the cell's host card.
        if (
          sel?.selectionType === 'cell' &&
          ids.size === 1 &&
          ids.has(sel.cardId)
        ) {
          return;
        }

        const currentIds =
          sel?.selectionType === 'card'
            ? new Set(sel.cards.map(c => c.cardId))
            : new Set<string>();
        if (
          ids.size === currentIds.size &&
          [...ids].every(id => currentIds.has(id))
        ) {
          return;
        }
        if (ids.size === 0) {
          if (sel?.selectionType === 'card') {
            this.logic.selectionController.selection = undefined;
          }
          return;
        }
        const cards = [...ids]
          .map(cardId => {
            const groupKey = this.logic.findCardGroup(cardId);
            const rowKey = this.logic.findCardRow(cardId) ?? '';
            return groupKey ? { cardId, groupKey, rowKey } : null;
          })
          .filter(
            (v): v is { cardId: string; groupKey: string; rowKey: string } =>
              v !== null
          );
        if (cards.length === 0) return;
        this.logic.selectionController.selection = {
          selectionType: 'card',
          cards: cards as [
            { cardId: string; groupKey: string; rowKey: string },
            ...{ cardId: string; groupKey: string; rowKey: string }[],
          ],
        };
      })
    );
    this.disposables.add(
      effect(() => {
        const sel = this.logic.selection$.value;
        if (sel?.selectionType === 'card') {
          const selIds = new Set(sel.cards.map(c => c.cardId));
          const rsIds = this.logic.rowSelection.selectedIds$.value;
          if (
            selIds.size === rsIds.size &&
            [...selIds].every(id => rsIds.has(id))
          ) {
            return;
          }
          this.logic.rowSelection.setSelected(selIds);
          return;
        }
        if (sel?.selectionType === 'cell') {
          // Cell focus lives WITHIN one selected card. Keep the card
          // highlighted — either the multi-row set still stands (if
          // the cell's card is part of it) or we anchor a single-row
          // rowSelection on the cell's host card so newly-focused
          // cells still show the card as selected.
          const rsIds = this.logic.rowSelection.selectedIds$.value;
          if (rsIds.has(sel.cardId)) return;
          this.logic.rowSelection.setSelected(new Set([sel.cardId]));
          return;
        }
        // No selection or group selection — drop the row highlight.
        if (this.logic.rowSelection.selectedIds$.value.size > 0) {
          this.logic.rowSelection.setSelected(new Set());
        }
      })
    );
  }

  override disconnectedCallback(): void {
    this.sortContext.cancel();
    this.rowSortContext.cancel();
    this.canvasViewport.dispose();
    this.scrollStateCleanup?.();
    this.scrollStateCleanup = undefined;
    this.logic.reorder.cancel();
    if (this.reorderViewportRestoreFrame !== undefined) {
      cancelAnimationFrame(this.reorderViewportRestoreFrame);
      this.reorderViewportRestoreFrame = undefined;
    }
    this.ghostContainer?.remove();
    this.ghostContainer = null;
    this.ghostSeedCardId = null;
    this.dropPlaceholderHeight = 0;
    this.lastGhostDraggedIds = null;
    super.disconnectedCallback();
  }

  protected override firstUpdated(): void {
    this.syncIntrinsicLayoutAfterRender('first-render');
  }

  private syncIntrinsicLayoutAfterRender(
    _phase: 'first-render' | 'render-update'
  ): void {
    this.syncCanvasViewportParticipants();
    this.syncDataViewBodyResizeObserver({
      body: getDataViewBodyElement(this),
    });
    this.queueIntrinsicContentHeightChange();
  }

  protected override updated(): void {
    this.syncIntrinsicLayoutAfterRender('render-update');
  }

  prepareReorderGhost(
    seedCardId: string,
    evt: PointerEvent,
    captureTarget: HTMLElement
  ): void {
    const seed =
      captureTarget.closest<HTMLElement>('affine-data-view-kanban-card') ??
      this.querySelector<HTMLElement>(
        `affine-data-view-kanban-card[data-card-id="${CSS.escape(seedCardId)}"]`
      );
    if (!seed) {
      this.ghostSeedCardId = null;
      this.ghostSeedOffsetX = 0;
      this.ghostSeedOffsetY = 0;
      return;
    }

    const rect = seed.getBoundingClientRect();
    this.ghostSeedCardId = seedCardId;
    this.ghostSeedOffsetX = Math.max(
      0,
      Math.min(rect.width, evt.clientX - rect.left)
    );
    this.ghostSeedOffsetY = Math.max(
      0,
      Math.min(rect.height, evt.clientY - rect.top)
    );
  }

  prepareDropPlaceholder(
    snapshot: KanbanLayoutSnapshot,
    draggedIds: ReadonlySet<string>,
    visualScale: number
  ): void {
    const scale = visualScale || 1;
    let height = 0;
    let count = 0;
    for (const group of snapshot) {
      for (const card of group.cards) {
        if (!draggedIds.has(card.cardId)) {
          continue;
        }
        height += card.height / scale;
        count += 1;
      }
    }
    this.dropPlaceholderHeight =
      count > 0 ? height + Math.max(0, count - 1) * KANBAN_CARD_GAP : 0;
  }

  getDropPlaceholder(
    groupKey: string,
    rowKey: string
  ): { height: number; index: number } | undefined {
    const state = this.logic.reorder.state$.value;
    if (
      !state ||
      state.targetIndex === null ||
      state.dropScope !== `${rowKey}::${groupKey}` ||
      this.dropPlaceholderHeight <= 0
    ) {
      return undefined;
    }
    return {
      height: this.dropPlaceholderHeight,
      index: state.targetIndex,
    };
  }

  override getIntrinsicContentHeight(visualScale = 1): number {
    const empty = this.logic.view.rowCount$.value === 0;
    const bodyHeight = measureDataViewBodyIntrinsicHeight({
      body: getDataViewBodyElement(this),
      visualScale,
    });
    const rowsContainer = this.logic.scrollContainer$.value;
    const scale = visualScale || 1;
    const rowsHeight = rowsContainer
      ? measureKanbanRowsContentHeight({
          empty,
          rowsContainer,
          visualScale: scale,
        })
      : 0;
    return empty ? rowsHeight : Math.max(bodyHeight, rowsHeight);
  }

  applyCanvasViewportTarget(
    targetId: string,
    position: CanvasViewportTargetPosition
  ): boolean {
    return this.canvasViewport.applyTarget(targetId, position);
  }

  getCanvasViewportTargets(): Record<string, CanvasViewportTargetPosition> {
    return this.canvasViewport.getTargets();
  }

  captureReorderViewport(
    draggedIds: ReadonlySet<string>
  ): Record<string, CanvasViewportTargetPosition> {
    const targets = this.canvasViewport.getTargets();
    const container = this.logic.scrollContainer$.value;
    if (container) {
      targets[KANBAN_CANVAS_Y_SCROLL_TARGET] =
        readRowAnchoredCanvasViewportPosition(
          container,
          'y',
          this.getKanbanRowAnchorRects().filter(
            row => !draggedIds.has(row.rowId)
          )
        );
    }
    return targets;
  }

  restoreReorderViewport(
    snapshot: Readonly<Record<string, CanvasViewportTargetPosition>>
  ): void {
    if (this.reorderViewportRestoreFrame !== undefined) {
      cancelAnimationFrame(this.reorderViewportRestoreFrame);
    }
    this.requestUpdate();
    void this.updateComplete.then(() => {
      if (!this.isConnected) return;
      this.reorderViewportRestoreFrame = requestAnimationFrame(() => {
        this.reorderViewportRestoreFrame = undefined;
        this.syncCanvasViewportParticipants();
        for (const [targetId, position] of Object.entries(snapshot)) {
          this.canvasViewport.applyTarget(targetId, position);
        }
      });
    });
  }

  getVisualScale(): number {
    return getElementVisualScale(this, this.getBoundingClientRect());
  }

  private syncCanvasViewportParticipants(): void {
    const rowTargets = [
      ...this.querySelectorAll<HTMLElement>('.row-body-scroll[data-row-key]'),
    ].map(rowBodyScroll => {
      const rowKey = rowBodyScroll.dataset['rowKey'];
      if (!rowKey) {
        return undefined;
      }
      return createElementCanvasViewportTarget(
        `${KANBAN_CANVAS_ROW_X_SCROLL_TARGET_PREFIX}${rowKey}`,
        rowBodyScroll,
        'x'
      );
    });

    this.canvasViewport.sync([
      createRowAnchoredCanvasViewportTarget(
        KANBAN_CANVAS_Y_SCROLL_TARGET,
        this.logic.scrollContainer$.value,
        'y',
        () => this.getKanbanRowAnchorRects()
      ),
      createElementCanvasViewportTarget(
        KANBAN_CANVAS_FLAT_X_SCROLL_TARGET,
        this.querySelector<HTMLElement>(
          '[data-kanban-canvas-x-scroll="flat"]'
        ) ?? undefined,
        'x'
      ),
      ...rowTargets,
    ]);
  }

  private getKanbanRowAnchorRects(): CanvasViewportRowAnchorRect[] {
    const container = this.logic.scrollContainer$.value;
    if (!container) {
      return [];
    }
    const containerRect = container.getBoundingClientRect();
    const scale = getElementVisualScale(container, containerRect);
    const rows: CanvasViewportRowAnchorRect[] = [];
    const cards = this.querySelectorAll<HTMLElement>(
      'affine-data-view-kanban-card[data-card-id]'
    );
    for (const card of cards) {
      const rowId = card.dataset['cardId'];
      if (!rowId) {
        continue;
      }
      const rect = card.getBoundingClientRect();
      rows.push({
        height: rect.height / scale,
        left: (rect.left - containerRect.left) / scale,
        rowId,
        top: (rect.top - containerRect.top) / scale + container.scrollTop,
      });
    }
    return rows;
  }

  /**
   * One-shot snapshot of every visible kanban group's rect and all its
   * card rects at pointer-down. Used as the sole geometry source for
   * the drag session — live DOM reads mid-drag would include the
   * applied `translateY` and poison the hit-test.
   */
  captureKanbanRects(): KanbanLayoutSnapshot {
    const groups = this.querySelectorAll<HTMLElement>(
      'affine-data-view-kanban-group'
    );
    const out: KanbanGroupRect[] = [];
    for (const group of groups) {
      const groupKey = group.dataset['key'];
      const rowKey = group.dataset['rowKey'];
      if (!groupKey || rowKey == null) continue;
      const groupRect = group.getBoundingClientRect();
      const body = group.querySelector<HTMLElement>('.group-body');
      const bodyRect = body?.getBoundingClientRect();
      const cards: KanbanCardRect[] = [];
      const cardEls = group.querySelectorAll<HTMLElement>(
        'affine-data-view-kanban-card'
      );
      for (const card of cardEls) {
        const cardId = card.dataset['cardId'];
        if (!cardId) continue;
        const r = card.getBoundingClientRect();
        cards.push({
          cardId,
          top: r.top,
          bottom: r.bottom,
          left: r.left,
          height: r.height,
        });
      }
      out.push({
        groupKey,
        rowKey,
        scope: `${rowKey}::${groupKey}`,
        left: groupRect.left,
        right: groupRect.right,
        top: groupRect.top,
        bottom: groupRect.bottom,
        bodyTop: bodyRect?.top ?? groupRect.top,
        bodyLeft: bodyRect?.left ?? groupRect.left,
        cards,
      });
    }
    return out;
  }

  override render(): TemplateResult {
    const groups = this.logic.groups$.value;
    if (!groups) {
      return html``;
    }

    const vPadding = this.logic.root.config.virtualPadding$.value;
    const wrapperStyle = styleMap({
      marginLeft: `-${vPadding}px`,
      marginRight: `-${vPadding}px`,
      paddingLeft: `${vPadding}px`,
      paddingRight: `${vPadding}px`,
    });

    return html`
      <div
        data-data-view-body="true"
        data-data-view-body-state=${dataViewBodyStateValue('data')}
        data-kanban-empty=${this.logic.view.rowCount$.value === 0
          ? 'true'
          : 'false'}
        class="${kanbanBodyStyle}"
        style="${wrapperStyle}"
      >
        <div
          ${ref(this.logic.scrollContainer$)}
          data-kanban-rows="true"
          data-data-view-body-content="true"
          data-data-view-body-intrinsic="true"
          data-data-view-body-render-content="true"
          data-data-view-body-render-root="true"
          class="${kanbanRowsStyle}"
          @wheel="${this.logic.onWheel}"
        >
          ${this.renderRows()}
        </div>
      </div>
    `;
  }
}

const kanbanViewStyle = css({
  userSelect: 'none',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  maxHeight: '100%',
  overflow: 'hidden',

  '& > :first-child': {
    flexShrink: 0,
  },
});

const kanbanBodyStyle = css({
  position: 'relative',
  zIndex: 1,
  flex: '1 1 auto',
  minHeight: 0,
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const kanbanRowsStyle = css({
  position: 'relative',
  zIndex: 1,
  flex: '1 1 auto',
  minHeight: 0,
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingBottom: '4px',
  overflowX: 'hidden',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  ...slimDataViewScrollbarStyle,

  '&::-webkit-scrollbar': {
    WebkitAppearance: 'none',
    display: 'block',
  },

  '&::-webkit-scrollbar:horizontal': {
    height: '4px',
  },

  '&::-webkit-scrollbar:vertical': {
    width: '4px',
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '2px',
    backgroundColor: 'transparent',
  },

  '&:hover::-webkit-scrollbar:horizontal': {
    height: '8px',
  },

  '&:hover::-webkit-scrollbar:vertical': {
    width: '8px',
  },

  '&:hover::-webkit-scrollbar-thumb': {
    borderRadius: '16px',
    backgroundColor: 'var(--streamlain-black-30)',
  },

  '&:hover::-webkit-scrollbar-track': {
    backgroundColor: 'var(--streamlain-hover-color)',
  },
});

const kanbanFlatOuterStyle = css({
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-start',
  minWidth: 0,
  overflow: 'visible',
});

const kanbanFlatColumnsStyle = css({
  position: 'relative',
  flex: '1 1 auto',
  minWidth: 0,
});

const kanbanFlatScrollerStyle = css({
  display: 'flex',
  gap: '20px',
  overflowX: 'auto',
  overflowY: 'hidden',
  overscrollBehaviorX: 'contain',
  alignItems: 'flex-start',
  ...slimDataViewScrollbarStyle,
});

const kanbanFlatStickyHeaderStyle = css({
  position: 'sticky',
  top: 0,
  zIndex: 10,
  height: `${KANBAN_GROUP_HEADER_HEIGHT}px`,
  marginBottom: '4px',
  overflow: 'clip',
  background:
    'var(--streamlain-kanban-group-header-background, var(--streamlain-background-secondary-color))',
  borderRadius: '8px',
  pointerEvents: 'none',
  isolation: 'isolate',

  '.kanban-view[data-kanban-y-scrolled="true"] &': {
    borderRadius: '0px',
  },
});

const kanbanFlatStickyHeaderInnerStyle = css({
  display: 'flex',
  gap: '20px',
  width: 'max-content',
  minWidth: '100%',
  pointerEvents: 'auto',
  willChange: 'transform',
});

// Overflow tile shown when the render window cuts off columns, row
// groups, or cards. Clicking widens the window by one policy step —
// the board stays sample-rendered, the user pulls in more on demand.
const kanbanOverflowTileStyle = css({
  width: 'var(--kanban-column-width, 260px)',
  flexShrink: 0,
  border: '1px solid var(--streamlain-border-color)',
  borderRadius: '8px',
  color: 'var(--streamlain-text-secondary-color)',
  padding: '12px',
  textAlign: 'center',
  cursor: 'pointer',
  userSelect: 'none',

  '&:hover': {
    backgroundColor: 'var(--streamlain-hover-color)',
    color: 'var(--streamlain-text-primary-color)',
  },
});

const addGroupIconStyle = css({
  padding: '4px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  color: 'var(--streamlain-text-secondary-color)',

  '&:hover': {
    backgroundColor: 'var(--streamlain-hover-color)',
  },

  '&[data-readonly="true"]': {
    cursor: 'default',
    opacity: 0.45,
    pointerEvents: 'none',
  },

  '& svg': {
    width: '16px',
    height: '16px',
    fill: 'var(--streamlain-icon-color)',
    color: 'var(--streamlain-icon-color)',
  },
});

// Narrow "add column" button left of all kanban lanes in 1D mode.
// Lives OUTSIDE the inner overflow-x:auto scroll div so position:sticky
// resolves against kanbanRowsStyle (the outer scroll-y ancestor).
// top: calc(50vh - 20px) keeps the button vertically centred in the
// visible viewport regardless of how far the user has scrolled down.
const addGroupColumnStyle = css({
  width: '36px',
  flexShrink: 0,
  alignSelf: 'flex-start',
  position: 'sticky',
  top: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 0',
  borderRadius: '8px',
  cursor: 'pointer',
  border: '2px dashed transparent',
  color: 'var(--streamlain-text-secondary-color)',
  transition:
    'background-color 150ms cubic-bezier(0.42,0,1,1), border-color 150ms cubic-bezier(0.42,0,1,1), color 150ms',

  '&:hover': {
    backgroundColor: 'var(--streamlain-hover-color)',
    borderColor: 'var(--streamlain-border-color)',
    color: 'var(--streamlain-text-primary-color)',
  },

  '&[data-readonly="true"]': {
    cursor: 'default',
    opacity: 0.45,
    pointerEvents: 'none',
  },

  '& svg': {
    width: '16px',
    height: '16px',
    fill: 'currentColor',
    color: 'currentColor',
  },
});

declare global {
  interface HTMLElementTagNameMap {
    'dv-kanban-view-ui': KanbanViewUI;
  }
}
